/**
 * webPush — browser side of Web Push (CEL-2450, 2026-09-09; account model CEL-2490, same evening).
 *
 * The server (services/web_push_service.py) decides WHEN a stop deserves a notification and whether the ACCOUNT
 * wants any (User.notification_settings.browser_push: undecided / on / off). This module manages THIS browser:
 * feature support, the one permission request (a click, as Firefox and Safari require), get-or-create + register the
 * subscription against /user/push/*, unregister at sign-out, and the "this device was offered" flag for the in-chat
 * card. The service worker (public/sw.js) renders the notification and opens the chat on tap.
 *
 * The three layers, so nobody confuses them again (agent_docs/notifications_journey_review_2026-09-09.md §0):
 *   account setting    -> server; follows the person to every device (the Settings switch, the card's Turn on / X)
 *   browser permission -> per browser profile, per device; only a click can request it; nothing syncs it
 *   registration       -> one push_subscriptions row per browser, under the signed-in user; forgotten at sign-out
 *
 * Support notes: Chrome/Edge/Firefox desktop + Android support push in the tab. iOS Safari supports it only when
 * the site is installed to the home screen (no PushManager otherwise) — isIosNeedsInstall() tells the UI to say so
 * instead of prompting.
 */
import { getPushPublicKey, removePushSubscription, savePushSubscription } from '../services/userApi';

const SW_URL = '/sw.js';
const DEVICE_ASKED_KEY = 'cellcog_push_prompt_seen';

/** The ACCOUNT setting values (User.notification_settings.browser_push). Server-side truth. */
export const PUSH_ACCOUNT = { UNDECIDED: 'undecided', ON: 'on', OFF: 'off' };
/** Tap tracking: a page opened or navigated by a tap carries ?src=push; a page the worker focused gets a message. */
export const TAP_QUERY_PARAM = 'src';
export const TAP_QUERY_VALUE = 'push';
export const TAP_MESSAGE_TYPE = 'cc-push-tapped';

let publicKeyPromise = null;

/** 'undecided' | 'on' | 'off' from the profile; anything else reads as undecided. */
export const accountPushState = (profile) => {
  const value = profile && profile.notification_settings ? profile.notification_settings.browser_push : null;
  return value === PUSH_ACCOUNT.ON || value === PUSH_ACCOUNT.OFF ? value : PUSH_ACCOUNT.UNDECIDED;
};

export const isPushSupported = () =>
  typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const isIosNeedsInstall = () => {
  if (typeof navigator === 'undefined') return false;
  // iPadOS Safari defaults to "desktop mode": the UA and navigator.platform read as a Mac, only the touch
  // points give it away (same quirk utils/platform.isApplePlatform handles). Without this an iPad got neither
  // the card nor the Home Screen hint (Nitish, 2026-09-09).
  const ipadAsMac = navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
  const ios = ipadAsMac || /iPhone|iPad|iPod/.test(navigator.userAgent || '');
  return ios && !isPushSupported();
};

/** 'granted' | 'denied' | 'default' | 'unsupported' */
export const getPushPermission = () => (isPushSupported() ? Notification.permission : 'unsupported');

/** Server public key (VAPID) or null when push is not enabled on the server. Cached for the session. */
export const fetchPushPublicKey = () => {
  if (!publicKeyPromise) {
    publicKeyPromise = getPushPublicKey()
      .then((response) => (response && response.data && response.data.public_key) || null)
      .catch(() => null);
  }
  return publicKeyPromise;
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

const registration = async () => {
  await navigator.serviceWorker.register(SW_URL);
  return navigator.serviceWorker.ready;
};

export const currentPushSubscription = async () => {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  if (!reg) return null;
  return reg.pushManager.getSubscription();
};

/**
 * Register THIS browser under the signed-in account: get the existing subscription or create one, then upsert it on
 * the server (idempotent; an endpoint a previous account left on this machine is re-homed). Never prompts: needs the
 * permission granted already. This is the self-heal for Chrome's quiet bubble (granted after the card gave up),
 * cleared site data, endpoint rotation and a shared machine (hooks/usePushAccountSync runs it on load when the account
 * is on). Resolves true when the server holds this browser's row.
 */
export const ensureSubscribed = async () => {
  if (!isPushSupported() || Notification.permission !== 'granted') return false;
  const key = await fetchPushPublicKey();
  if (!key) return false;
  const reg = await registration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) });
  }
  await savePushSubscription(sub.toJSON());
  return true;
};

/**
 * The full opt-in for THIS device: the browser's permission prompt (from a click) → ensureSubscribed.
 * Resolves { granted: boolean, permission }. Throws only for unsupported/disabled (callers check first).
 */
export const requestAndSubscribe = async () => {
  if (!isPushSupported()) throw new Error('push-unsupported');
  const key = await fetchPushPublicKey();
  if (!key) throw new Error('push-disabled');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { granted: false, permission };
  const granted = await ensureSubscribed();
  return { granted, permission };
};

/**
 * Sign-out (journey review gap 3): forget THIS browser's registration on the server so the next account on this
 * machine never receives the previous one's notifications. The browser keeps its permission and its subscription
 * object; the next sign-in re-registers it (ensureSubscribed) when that account is on.
 */
export const unregisterThisBrowser = async () => {
  const sub = await currentPushSubscription();
  if (!sub) return false;
  await removePushSubscription(sub.endpoint);
  return true;
};

/**
 * Same-device clear: close the system notification for a chat or employee once the person is looking at it (Nitish,
 * Sep 9 2026). Tags mirror services/web_push_service.payload_for_stop: `cc-chat-<chat id>` for chats,
 * `cc-emp-<employee id>` for AI employees (one card per employee across shifts). Fire-and-forget; free (no push).
 */
export const clearNotificationFor = ({ chatId, employeeId } = {}) => {
  if (!isPushSupported()) return;
  const tag = employeeId ? `cc-emp-${employeeId}` : chatId ? `cc-chat-${chatId}` : null;
  if (!tag) return;
  navigator.serviceWorker.getRegistration(SW_URL)
    .then((reg) => (reg ? reg.getNotifications({ tag }) : []))
    .then((list) => list.forEach((n) => n.close()))
    .catch(() => {});
};

/** Per-BROWSER bookkeeping: this device was offered once (either card, or the iOS hint). The account decision itself
 * lives on the server (browser_push); this flag only stops the same device from asking twice. */
export const wasDeviceAsked = () => {
  try {
    return window.localStorage.getItem(DEVICE_ASKED_KEY) === '1';
  } catch (e) {
    return true; // no storage = never nag
  }
};
export const markDeviceAsked = () => {
  try {
    window.localStorage.setItem(DEVICE_ASKED_KEY, '1');
  } catch (e) {
    /* ignore */
  }
};
